import { ENTITY_TYPE } from '../../constants'
import { getTransactionDatabaseManager } from '../IndexDB/TransactionDatabaseManager'
import { getSimpleSmartSync } from './SimpleSmartSync'
import {
  refreshMerchantTransactions as runMerchantRefresh,
  IRefreshResult
} from './refreshMerchantTransactions'
export interface ISyncResult {
  success: boolean
  data: any[]
  error?: string
}

export interface ISyncOptions {
  orderBy?: number
}

class TransactionSyncService {
  // Use arrow functions to preserve 'this' context
  syncTransactions = async (
    entityId: string,
    entityType: ENTITY_TYPE,
    options: ISyncOptions = {}
  ): Promise<ISyncResult> => {
    try {
      // Use the simple smart sync strategy
      const simpleSmartSync = getSimpleSmartSync()
      const result = await simpleSmartSync.syncTransactions(
        entityId,
        entityType,
        options.orderBy || 3
      )

      return {
        success: result.success,
        data: result.data,
        error: result.error
      }
    } catch (error) {
      console.error('[SyncService] Error in simple smart sync:', error)
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Incremental pull-to-refresh: `type:0` GetTransactions calls fetch every
   * CUSTOMER and SUPPLIER transaction changed since each type's business-wide
   * `update_time` cursor and merge-upsert the deltas into IndexedDB. Cheap to
   * call on demand.
   */
  refreshMerchantTransactions = async (entityTypes?: ENTITY_TYPE[]): Promise<IRefreshResult> => {
    return runMerchantRefresh(entityTypes)
  }

  clearCache = async (entityId: string, entityType: ENTITY_TYPE): Promise<void> => {
    try {
      const simpleSmartSync = getSimpleSmartSync()
      await simpleSmartSync.clearCache(entityId, entityType)
    } catch (error) {
      console.error('Error clearing cache:', error)
      throw error
    }
  }

  clearAllCache = async (): Promise<void> => {
    try {
      const simpleSmartSync = getSimpleSmartSync()
      await simpleSmartSync.clearAllCache()
    } catch (error) {
      console.error('Error clearing all cache:', error)
      throw error
    }
  }

  getCachedTransactions = async (entityId: string, entityType: ENTITY_TYPE): Promise<any[]> => {
    try {
      const dbManager = getTransactionDatabaseManager()
      await dbManager.init()
      return await dbManager.getTransactionsByEntity(entityId, entityType)
    } catch (error) {
      console.error('Error getting cached transactions:', error)
      return []
    }
  }
}

// Singleton instance
let transactionSyncServiceInstance: TransactionSyncService | null = null

export const getTransactionSyncService = (): TransactionSyncService => {
  if (!transactionSyncServiceInstance) {
    transactionSyncServiceInstance = new TransactionSyncService()
  }
  return transactionSyncServiceInstance
}

export default TransactionSyncService
